import * as React from "react";
import type {
  Alert,
  AlertCounts,
  AlertCategory,
  AlertsApiResponse,
} from "@/lib/types/alerts";
import { getAlerts, dismissAlert, dismissAllAlerts } from "@/lib/api/alerts";
import { logger } from "@/lib/utils/logger";

export interface UseAlertsOptions {
  autoLoad?: boolean;
}

export interface UseAlertsReturn {
  alerts: Alert[];
  counts: AlertCounts;
  isLoading: boolean;
  error: string | null;
  dismiss: (alertKey: string) => Promise<void>;
  dismissAll: () => Promise<void>;
  refetch: () => Promise<void>;
  filterByCategory: (category: AlertCategory | "all") => Alert[];
}

const EMPTY_COUNTS: AlertCounts = {
  total: 0,
  byCategory: { compliance: 0, expiration: 0, stock: 0, subscription: 0 },
  bySeverity: { critical: 0, high: 0, medium: 0, low: 0 },
};

/**
 * Hook for fetching, filtering, and managing alerts.
 * Used by both the /alerts page and the notification bell in the layout.
 */
export function useAlerts(options: UseAlertsOptions = {}): UseAlertsReturn {
  const { autoLoad = true } = options;

  const [alerts, setAlerts] = React.useState<Alert[]>([]);
  const [counts, setCounts] = React.useState<AlertCounts>(EMPTY_COUNTS);
  const [isLoading, setIsLoading] = React.useState(autoLoad);
  const [error, setError] = React.useState<string | null>(null);

  const fetchingRef = React.useRef(false);

  const fetchAlerts = React.useCallback(async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      const raw = await getAlerts();
      // API wraps the payload in { success, data: { alerts, counts } }
      const result = ((raw as { data?: AlertsApiResponse }).data ??
        raw) as AlertsApiResponse;

      setAlerts(result.alerts ?? []);
      setCounts(result.counts ?? EMPTY_COUNTS);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to fetch alerts";
      setError(message);
      logger.error("Fetch alerts failed", err, { operation: "fetch_alerts" });
    } finally {
      setIsLoading(false);
      fetchingRef.current = false;
    }
  }, []);

  // Auto-load on mount
  React.useEffect(() => {
    if (autoLoad) {
      fetchAlerts();
    }
  }, [fetchAlerts, autoLoad]);

  const dismiss = React.useCallback(
    async (alertKey: string) => {
      try {
        await dismissAlert({ alertKey });

        // Optimistic update: remove the dismissed alert
        setAlerts((prev) => {
          const updated = prev.filter((a) => a.alertKey !== alertKey);
          // Recompute counts
          const newCounts: AlertCounts = {
            total: updated.length,
            byCategory: {
              compliance: 0,
              expiration: 0,
              stock: 0,
              subscription: 0,
            },
            bySeverity: { critical: 0, high: 0, medium: 0, low: 0 },
          };
          for (const a of updated) {
            newCounts.byCategory[a.category]++;
            newCounts.bySeverity[a.severity]++;
          }
          setCounts(newCounts);
          return updated;
        });
      } catch (err) {
        logger.error("Dismiss alert failed", err, {
          operation: "dismiss_alert",
        });
        // Refetch to get the actual state
        await fetchAlerts();
      }
    },
    [fetchAlerts]
  );

  const dismissAll = React.useCallback(async () => {
    try {
      if (alerts.length === 0) return;

      await dismissAllAlerts();

      // Optimistic update
      setAlerts([]);
      setCounts(EMPTY_COUNTS);
    } catch (err) {
      logger.error("Dismiss all alerts failed", err, {
        operation: "dismiss_all_alerts",
      });
      await fetchAlerts();
    }
  }, [alerts, fetchAlerts]);

  const filterByCategory = React.useCallback(
    (category: AlertCategory | "all"): Alert[] => {
      if (category === "all") return alerts;
      return alerts.filter((a) => a.category === category);
    },
    [alerts]
  );

  return {
    alerts,
    counts,
    isLoading,
    error,
    dismiss,
    dismissAll,
    refetch: fetchAlerts,
    filterByCategory,
  };
}
